import type { Group, Nature } from "../lib/api";

export const NATURE: Record<Nature, string> = { A: "Assets", L: "Liabilities", I: "Income", E: "Expenses" };

export interface TreeRow { group: Group; depth: number }

// the group tree in reading order: primaries by their place, sub-groups under their parent by name
export function groupTree(groups: Group[]): TreeRow[] {
  const kids = new Map<string | null, Group[]>();
  for (const g of groups) {
    const k = g.parent_id;
    if (!kids.has(k)) kids.set(k, []);
    kids.get(k)!.push(g);
  }
  for (const list of kids.values())
    // numbered groups in number order, then the rest by name; top-level ones by the chart's order
    list.sort((a, b) => (a.parent_id === null ? a.sort - b.sort : 0)
      || (a.number && b.number ? a.number.localeCompare(b.number) : a.number ? -1 : b.number ? 1 : 0)
      || a.name.localeCompare(b.name));
  const out: TreeRow[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number) => {
    for (const g of kids.get(parent) ?? []) {
      if (seen.has(g.id)) continue;
      seen.add(g.id);
      out.push({ group: g, depth });
      walk(g.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

// a group and everything under it (a group can't move under its own descendant)
export function descendants(groups: Group[], id: string): Set<string> {
  const out = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const g of groups) if (g.parent_id && out.has(g.parent_id) && !out.has(g.id)) { out.add(g.id); grew = true; }
  }
  return out;
}

export const under = (g: Group | undefined, code: string) => !!g && g.codes.includes(code);

// what a ledger under this group is: drives which details the form shows
export function ledgerKind(g: Group | undefined) {
  return {
    party: under(g, "trade_receivables") || under(g, "trade_payables"),
    partyKind: under(g, "trade_receivables") ? "customer" : under(g, "trade_payables") ? "supplier" : null,
    bank: under(g, "bank_accounts") || under(g, "bank_od"),
    duties: under(g, "statutory_dues") || under(g, "loans_advances"),   // GST: output and reverse charge owed, input credit held
    pnl: g?.nature === "I" || g?.nature === "E",
  } as const;
}
